// Phase 129 — SSE replacement for use-demo-socket.ts.
//
// Subscribes to web-api Server-Sent Events:
//   GET /demo/stream?claim=<publicId>
//
// Server → browser events:
//   - `ready`         ({ code, demoSessionId })       — fresh session
//   - `bound`         ({ demoSessionId, sandboxSessionPublicId, boundPhone })
//   - `inbound`       ({ from, text })
//   - `closed`        ({ reason })                    — server ends; auto-reconnect
//   - `taken_over`    ({ reason })                    — server ends; suppress reconnect
//   - `phone_claimed` ({ message })                   — Phase 130 (Rule 1 + Codes
//                                                       Unification, Task 8): the
//                                                       phone is now adopted by a
//                                                       HookMyApp account; server
//                                                       ends; suppress reconnect
//
// Browser → server (outbound):
//   - POST /demo/reply  { demoSessionId, text }     — replaces socket emit
//   - POST /demo/reclaim { sandboxSessionPublicId } — multi-tab "Use Here"
//
// localStorage seamless reconnect:
//   key:   `hookmyapp:demo:bind`
//   value: `{ sandboxSessionPublicId, boundPhone, savedAt }`
// Persisted on first `event: bound`. Cleared on user reset.
//
// Test seam preserved from the WS hook:
//   - `window.__mockSocketEmit(event, payload)` synthesizes server events
//     so Playwright doesn't need a running web-api.
//   - `window.__lastEmitted = { event, payload }` records the outbound
//     POST so tests can assert the user's reply went out.
import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'hookmyapp:demo:bind';

export interface DemoMessage {
  from: string;
  text: string;
  ts: number;
  outbound?: boolean;
}

/**
 * Phase 131 (Multi-Channel Instagram, Task 11). Provider-aware bound sender
 * read off the additive `bound` event payload (`senderType`, `senderId`,
 * `senderAccountId`). The legacy `boundPhone` field stays for WhatsApp-only
 * chrome that already reads it; new readers should branch on
 * `boundSender.senderType` to pick provider-specific UI.
 */
export interface BoundSender {
  senderType: 'whatsapp' | 'instagram';
  senderId: string;
  senderAccountId: string;
}

export interface DemoStreamState {
  code: string | null;
  demoSessionId: string | null;
  messages: DemoMessage[];
  bound: boolean;
  closed: boolean;
  takenOver: boolean;
  /**
   * Phase 130 (Rule 1 + Codes Unification, Task 9). Set true when the server
   * emits a one-shot `phone_claimed` event — the phone tied to this demo's
   * sandboxSessionPublicId has been adopted by a signed-up HookMyApp account.
   * The server closes the stream after the event; the UI swaps to a terminal
   * "claimed" state with a sign-in CTA. There is no recovery path from here:
   * "Use Here" would dead-end, so we don't show it.
   */
  phoneClaimed: boolean;
  /**
   * The user-facing message that accompanied the `phone_claimed` event. Shown
   * verbatim in the claimed-state card. Empty string when no event has fired.
   */
  phoneClaimedMessage: string;
  boundPhone: string | null;
  /**
   * Phase 131 — provider-aware bound sender. Null until the `bound` event
   * arrives. Populated additively (the WA legacy `boundPhone` field continues
   * to fill in parallel for WhatsApp binds).
   */
  boundSender: BoundSender | null;
  sendReply: (text: string) => void;
  reclaim: () => Promise<void>;
  resetBind: () => void;
}

interface ReadyPayload {
  code: string;
  demoSessionId: string;
}

interface BoundPayload {
  demoSessionId: string;
  sandboxSessionPublicId: string;
  /** Legacy WA-shaped field — still emitted by web-api for WhatsApp binds. */
  boundPhone: string;
  /**
   * Phase 131 additive fields. Optional on the wire because (a) older builds
   * of web-api may not emit them and (b) Task 3 kept the SandboxSessionEvent
   * payload additive. When present, drive provider-aware chrome.
   */
  senderType?: 'whatsapp' | 'instagram';
  senderId?: string;
  senderAccountId?: string;
  /**
   * First-bind transcript — the inbound code the user sent plus the welcome
   * reply, carried on `bound` so both bubbles survive the post-bind SSE source
   * swap (a follow-on `inbound` event would race the teardown). Absent on
   * reclaim binds, so the handler only seeds when present.
   */
  transcript?: Array<{ from?: string; text: string; outbound?: boolean }>;
}

interface InboundPayload {
  from: string;
  text: string;
}

interface PhoneClaimedPayload {
  message: string;
}

interface MockEmitWindow {
  __mockSocketEmit?: (event: string, payload: unknown) => void;
  __lastEmitted?: { event: string; payload: unknown };
  // Set true once the hook has installed the post-mount handler and drained
  // the pending queue. Playwright must wait on this before injecting events,
  // otherwise the live SSE handshake can race the test seam (and dev-server
  // hydration can destroy the JS context mid-evaluate).
  __demoStreamMounted?: boolean;
}

interface StoredBind {
  sandboxSessionPublicId: string;
  boundPhone: string;
  savedAt: number;
}

// Module-level pending event queue. `__mockSocketEmit` calls before the hook
// mounts get queued and replayed once the hook has registered its handler.
type PendingEvent = { event: string; payload: unknown };
const pendingMockEmits: PendingEvent[] = [];

if (typeof window !== 'undefined') {
  const w = window as Window & MockEmitWindow;
  if (typeof w.__mockSocketEmit !== 'function') {
    w.__mockSocketEmit = (event, payload) => {
      pendingMockEmits.push({ event, payload });
    };
  }
}

function readStoredBind(): StoredBind | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredBind;
    if (typeof parsed?.sandboxSessionPublicId !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredBind(value: StoredBind | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* private mode / quota — fall through, demo still works without persistence */
  }
}

function getWebApiBaseUrl(): string {
  return import.meta.env.VITE_WEB_API_URL ?? '';
}

export function useDemoStream(): DemoStreamState {
  const [code, setCode] = useState<string | null>(null);
  const [demoSessionId, setDemoSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<DemoMessage[]>([]);
  const [bound, setBound] = useState(false);
  const [closed, setClosed] = useState(false);
  const [takenOver, setTakenOver] = useState(false);
  const [phoneClaimed, setPhoneClaimed] = useState(false);
  const [phoneClaimedMessage, setPhoneClaimedMessage] = useState('');
  const [boundPhone, setBoundPhone] = useState<string | null>(() => readStoredBind()?.boundPhone ?? null);
  const [boundSender, setBoundSender] = useState<BoundSender | null>(null);
  const sourceRef = useRef<EventSource | null>(null);
  const demoSessionIdRef = useRef<string | null>(null);
  const sandboxPublicIdRef = useRef<string | null>(null);

  const closeStream = useCallback(() => {
    if (sourceRef.current) {
      sourceRef.current.close();
      sourceRef.current = null;
    }
  }, []);

  const openStream = useCallback((claim?: string) => {
    closeStream();
    const baseUrl = getWebApiBaseUrl();
    const url = claim
      ? `${baseUrl}/demo/stream?claim=${encodeURIComponent(claim)}`
      : `${baseUrl}/demo/stream`;

    // Phase 129 Codex review hardening — withCredentials:true makes the
    // browser send the HttpOnly reclaim cookie on this SSE handshake.
    // Without it, the cookie is dropped on cross-origin EventSource opens
    // (production: web at staging-web.hookmyapp.com → web-api at
    // staging-web-api.hookmyapp.com). Local dev hits the Vite proxy and is
    // same-origin, so withCredentials is a no-op there but harmless.
    const source = new EventSource(url, { withCredentials: true });
    sourceRef.current = source;

    source.addEventListener('ready', (ev) => {
      const payload = JSON.parse((ev as MessageEvent).data) as ReadyPayload;
      const previousId = demoSessionIdRef.current;
      setCode(payload.code);
      setDemoSessionId(payload.demoSessionId);
      demoSessionIdRef.current = payload.demoSessionId;
      // On a genuine session swap (post-reconnect after `closed`), drop the
      // stale local bind state so the user sees the new code card. Initial
      // mount has previousId === null and falls through unchanged.
      if (previousId && previousId !== payload.demoSessionId) {
        writeStoredBind(null);
        sandboxPublicIdRef.current = null;
        setBound(false);
        setClosed(false);
        setTakenOver(false);
        setMessages([]);
        setBoundPhone(null);
        setBoundSender(null);
      }
    });

    source.addEventListener('bound', (ev) => {
      const payload = JSON.parse((ev as MessageEvent).data) as BoundPayload;
      // Was the URL we opened with already a claim URL? If yes, the native
      // EventSource auto-reconnect will already use that URL on transport
      // drop (Cloud Run's 300-s request timeout) — no swap needed. Only
      // first-bind opens against /demo/stream (no claim) and needs the
      // close+reopen below.
      const isAlreadyClaimUrl = source.url.includes('?claim=');

      setBound(true);
      setClosed(false);
      setTakenOver(false);
      setDemoSessionId(payload.demoSessionId);
      demoSessionIdRef.current = payload.demoSessionId;
      sandboxPublicIdRef.current = payload.sandboxSessionPublicId;
      setBoundPhone(payload.boundPhone);
      // Phase 131 — provider-aware bound sender. Default to a WhatsApp
      // shape derived from the legacy `boundPhone` when web-api hasn't yet
      // emitted the prefixed triplet (additive contract; older deploys keep
      // working). When the triplet is present, drive provider-specific UI.
      if (payload.senderType) {
        setBoundSender({
          senderType: payload.senderType,
          senderId: payload.senderId ?? payload.boundPhone,
          senderAccountId: payload.senderAccountId ?? '',
        });
      } else if (payload.boundPhone) {
        setBoundSender({
          senderType: 'whatsapp',
          senderId: payload.boundPhone,
          senderAccountId: '',
        });
      }
      writeStoredBind({
        sandboxSessionPublicId: payload.sandboxSessionPublicId,
        boundPhone: payload.boundPhone,
        savedAt: Date.now(),
      });

      // Seed the first-bind transcript (inbound code + welcome reply). Only
      // present on the first `bound`; the reclaim `bound` omits it, so the
      // existing conversation is never clobbered.
      if (payload.transcript && payload.transcript.length > 0) {
        setMessages(
          payload.transcript.map((t) => ({
            from: t.from ?? (t.outbound ? 'bot' : 'them'),
            text: t.text,
            ts: Date.now(),
            outbound: !!t.outbound,
          })),
        );
      }

      // Phase 130 — staging bug fix. EventSource's native auto-reconnect
      // re-opens the URL the source was constructed with; JS cannot
      // intercept the reconnect to swap URLs. So when the page first loads
      // and opens `/demo/stream` (no claim), the auto-reconnect after Cloud
      // Run's 300-s timeout would land back at `/demo/stream` — server
      // treats as a fresh visitor and mints a new code. The user sees a
      // fresh code "as if they never sent the previous one."
      //
      // Fix: as soon as we're bound, replace the source with one
      // constructed against `?claim=<publicId>`. From this point, every
      // native auto-reconnect goes through `openClaimStream`, which reuses
      // the same SandboxSession via cookie auth. Skip the swap when we're
      // already on a claim URL (e.g. reclaim path that re-emits `bound`).
      if (!isAlreadyClaimUrl) {
        // Defer to a microtask so the current `bound` handler unwinds before
        // we tear down the source — closing inside the handler can race the
        // SSE reader on some browsers.
        Promise.resolve().then(() =>
          openStream(payload.sandboxSessionPublicId),
        );
      }
    });

    source.addEventListener('inbound', (ev) => {
      const payload = JSON.parse((ev as MessageEvent).data) as InboundPayload;
      setBound(true);
      setMessages((m) => [
        ...m,
        { from: payload.from, text: payload.text, ts: Date.now() },
      ]);
    });

    // Server-pushed outbound message (e.g. the text-only notice) — mirrors a
    // bot reply into the chat as an outbound bubble.
    source.addEventListener('outbound', (ev) => {
      const payload = JSON.parse((ev as MessageEvent).data) as { text: string };
      setBound(true);
      setMessages((m) => [
        ...m,
        { from: 'bot', text: payload.text, ts: Date.now(), outbound: true },
      ]);
    });

    source.addEventListener('closed', () => {
      setClosed(true);
      // Phase 129 Plan 09 (Codex review hardening, third pass): native
      // EventSource auto-reconnect always reopens the SAME URL the source
      // was constructed with. If the page first opened `/demo/stream` (no
      // claim) and then bound, the auto-reconnect would silently drop back
      // to a fresh unclaimed session — the user would lose their bound
      // chat continuity even though we have a sandboxSessionPublicId in
      // localStorage. Take control of the reconnect: close the current
      // source and re-open with `?claim=<publicId>` so the server emits
      // `event: bound` and the chat panel stays put. Falls back to a fresh
      // stream when there's nothing to reclaim (preserves the existing
      // first-touch path).
      const stored = readStoredBind();
      const claim = stored?.sandboxSessionPublicId ?? sandboxPublicIdRef.current ?? undefined;
      // Defer to a microtask so the current `closed` handler fully unwinds
      // before we tear down the source — closing inside the handler can
      // race the SSE reader on some browsers.
      Promise.resolve().then(() => openStream(claim));
    });

    source.addEventListener('taken_over', () => {
      setTakenOver(true);
      // Server has ended the stream. Suppress EventSource auto-reconnect by
      // closing here — the takeover overlay's "Use Here" CTA is the only
      // path back.
      closeStream();
    });

    source.addEventListener('phone_claimed', (ev) => {
      // Phase 130 (Rule 1 + Codes Unification, Task 9). The phone tied to
      // this demo's sandboxSessionPublicId has been adopted by a signed-up
      // HookMyApp account. The server emits this one-shot event then closes
      // the stream. There is no "Use Here" recovery path — that would
      // dead-end. Suppress EventSource auto-reconnect, drop the stale
      // localStorage bind so a future page load gets a fresh code, and let
      // the UI render the terminal claimed-state card with the sign-in CTA.
      const payload = JSON.parse((ev as MessageEvent).data) as PhoneClaimedPayload;
      setPhoneClaimed(true);
      setPhoneClaimedMessage(payload.message);
      setTakenOver(false);
      writeStoredBind(null);
      sandboxPublicIdRef.current = null;
      closeStream();
    });

    // Plain `message` events (no `event:` line) shouldn't happen in our
    // server, but if any do, log to aid diagnosis without breaking the demo.
    source.onerror = () => {
      // Native EventSource auto-reconnect handles transient drops. If the
      // stream is permanently dead, the server already sent `taken_over` or
      // `closed` and our handlers above ran first.
    };
  }, [closeStream]);

  useEffect(() => {
    const stored = readStoredBind();
    openStream(stored?.sandboxSessionPublicId);

    // Test seam — Playwright drives the demo via window.__mockSocketEmit.
    // First touch from the test seam closes the live EventSource so the two
    // sources don't race on state — Playwright owns the timeline once it
    // fires. Subsequent test seam calls just synthesize state transitions
    // directly. This keeps both the offline path (Playwright with no live
    // web-api) and the online path (real EventSource only when no test seam
    // input has occurred) working without cross-contamination.
    let testSeamActive = false;
    const closeLiveOnTestSeam = (): void => {
      if (testSeamActive) return;
      testSeamActive = true;
      closeStream();
    };
    const handle = (event: string, payload: unknown): void => {
      closeLiveOnTestSeam();
      if (event === 'ready') {
        const p = payload as ReadyPayload;
        setCode(p.code);
        setDemoSessionId(p.demoSessionId);
        demoSessionIdRef.current = p.demoSessionId;
      } else if (event === 'bound') {
        const p = payload as BoundPayload;
        setBound(true);
        setDemoSessionId(p.demoSessionId);
        demoSessionIdRef.current = p.demoSessionId;
        sandboxPublicIdRef.current = p.sandboxSessionPublicId;
        if (p.senderType) {
          setBoundSender({
            senderType: p.senderType,
            senderId: p.senderId ?? p.boundPhone,
            senderAccountId: p.senderAccountId ?? '',
          });
        } else if (p.boundPhone) {
          setBoundSender({
            senderType: 'whatsapp',
            senderId: p.boundPhone,
            senderAccountId: '',
          });
        }
        // Mirror the live handler — seed the first-bind transcript when present.
        if (p.transcript && p.transcript.length > 0) {
          setMessages(
            p.transcript.map((t) => ({
              from: t.from ?? (t.outbound ? 'bot' : 'them'),
              text: t.text,
              ts: Date.now(),
              outbound: !!t.outbound,
            })),
          );
        }
      } else if (event === 'inbound') {
        const p = payload as InboundPayload;
        setBound(true);
        setDemoSessionId((existing) => existing ?? 'demo_session_test');
        if (!demoSessionIdRef.current) {
          demoSessionIdRef.current = 'demo_session_test';
        }
        setCode((existing) => existing ?? 'TEST123');
        setMessages((m) => [...m, { from: p.from, text: p.text, ts: Date.now() }]);
      } else if (event === 'outbound') {
        const p = payload as { text: string };
        setMessages((m) => [
          ...m,
          { from: 'bot', text: p.text, ts: Date.now(), outbound: true },
        ]);
      } else if (event === 'closed') {
        setClosed(true);
      } else if (event === 'taken_over') {
        setTakenOver(true);
      } else if (event === 'phone_claimed') {
        // Test seam mirror of the live SSE handler above.
        const p = payload as PhoneClaimedPayload;
        setPhoneClaimed(true);
        setPhoneClaimedMessage(p.message);
        setTakenOver(false);
      }
    };
    const mockWin = window as Window & MockEmitWindow;
    mockWin.__mockSocketEmit = handle;
    while (pendingMockEmits.length > 0) {
      const { event, payload } = pendingMockEmits.shift()!;
      handle(event, payload);
    }
    mockWin.__demoStreamMounted = true;

    return () => {
      mockWin.__demoStreamMounted = false;
      closeStream();
    };
    // The hook intentionally runs once on mount; openStream/closeStream are
    // stable callback refs. Reopening on every render would thrash the SSE
    // connection.
  }, []);

  const sendReply = useCallback((text: string) => {
    const id = demoSessionIdRef.current;
    setMessages((m) => [...m, { from: 'you', text, ts: Date.now(), outbound: true }]);
    if (typeof window !== 'undefined') {
      const mockWin = window as Window & MockEmitWindow;
      mockWin.__lastEmitted = { event: 'demo:reply', payload: { text } };
    }
    if (!id) return; // test seam path — no real demoSessionId yet
    const baseUrl = getWebApiBaseUrl();
    void fetch(`${baseUrl}/demo/reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ demoSessionId: id, text }),
    }).catch(() => {
      /* network failure surfaces as a missing inbound on the chat — the
       * user can retry. The error is non-fatal for the demo as a whole. */
    });
  }, []);

  const resetBind = useCallback(() => {
    // User chose to bind a different number. Tell the server to tear down
    // the demo_anonymous SandboxSession (DELETEs the DB row, frees the
    // phone's partial-unique constraint, disposes the in-memory
    // DemoSession mapping) and clear the reclaim cookie. Then drop all
    // local state and re-open `/demo/stream` with no claim so the server
    // mints a fresh code + demoSessionId. Server call fails open — even
    // if it errors, the local reset still completes and the user gets a
    // new code; the orphan row ages out via the existing demo TTL.
    const claim = sandboxPublicIdRef.current ?? readStoredBind()?.sandboxSessionPublicId;
    const baseUrl = getWebApiBaseUrl();
    if (claim) {
      void fetch(`${baseUrl}/demo/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sandboxSessionPublicId: claim }),
      }).catch(() => {
        /* server teardown is best-effort; client-side reset proceeds */
      });
    }
    writeStoredBind(null);
    sandboxPublicIdRef.current = null;
    demoSessionIdRef.current = null;
    setBound(false);
    setClosed(false);
    setTakenOver(false);
    setPhoneClaimed(false);
    setPhoneClaimedMessage('');
    setMessages([]);
    setBoundPhone(null);
    setBoundSender(null);
    setCode(null);
    setDemoSessionId(null);
    openStream();
  }, [openStream]);

  const reclaim = useCallback(async () => {
    const stored = readStoredBind();
    const claim = stored?.sandboxSessionPublicId ?? sandboxPublicIdRef.current;
    if (!claim) return;
    const baseUrl = getWebApiBaseUrl();
    try {
      // credentials:'include' is REQUIRED for the HttpOnly reclaim cookie
      // to flow back to web-api. Without it, the request hits with no
      // cookie and AuthError(DEMO_CLAIM_NO_COOKIE) fires server-side.
      const res = await fetch(`${baseUrl}/demo/reclaim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sandboxSessionPublicId: claim }),
      });
      if (!res.ok) {
        // Reclaim failed — clear the bad localStorage value and bootstrap
        // a fresh session.
        writeStoredBind(null);
        sandboxPublicIdRef.current = null;
        setTakenOver(false);
        setBound(false);
        openStream();
        return;
      }
      // Phase 130 (Rule 1 + Codes Unification, Task 8 follow-up) — reclaim
      // pre-flight. When the previously-bound phone has since been adopted
      // by a signup, web-api short-circuits with `{ phoneClaimed: true }`
      // (cookie auth is bypassed because adoptDemoHandoff nulled the
      // claim_token_hash). Treat identically to the phone_claimed SSE
      // event so the overlay renders without bouncing through a fresh
      // stream that would just hit the same wall.
      const json = (await res.json().catch(() => null)) as
        | { ok: true; phoneClaimed?: true; message?: string }
        | null;
      if (json?.phoneClaimed) {
        setPhoneClaimed(true);
        setPhoneClaimedMessage(json.message ?? '');
        setTakenOver(false);
        writeStoredBind(null);
        sandboxPublicIdRef.current = null;
        return;
      }
    } catch {
      writeStoredBind(null);
      sandboxPublicIdRef.current = null;
      setTakenOver(false);
      setBound(false);
      openStream();
      return;
    }
    setTakenOver(false);
    openStream(claim);
  }, [openStream]);

  return {
    code,
    demoSessionId,
    messages,
    bound,
    closed,
    takenOver,
    phoneClaimed,
    phoneClaimedMessage,
    boundPhone,
    boundSender,
    sendReply,
    reclaim,
    resetBind,
  };
}
